import type { ImportColumnMapping, ImportStatus, ImportTarget } from "@astrum/shared";

/**
 * Pure helpers behind the import dialog's mapping step: one column maps to at
 * most one target, and most targets take only one column.
 */

/** Targets that may be filled by several columns (their values are combined). */
const REPEATABLE: ReadonlySet<string> = new Set(["assignee", "tags", "custom"]);

/** A choice in a column's dropdown: a plain target, or a custom field. */
export function choiceValue(entry: ImportColumnMapping | undefined): string {
  if (!entry) return "";
  return entry.target === "custom" ? `custom:${entry.fieldId ?? ""}` : entry.target;
}

export function parseChoice(value: string, column: number): ImportColumnMapping | null {
  if (!value) return null;
  if (value.startsWith("custom:")) return { column, target: "custom", fieldId: value.slice(7) };
  return { column, target: value as ImportTarget };
}

/**
 * Point a column at a target (or at nothing, with an empty choice). Choosing a
 * single-use target that another column already holds moves it here.
 */
export function setColumnChoice(columns: ImportColumnMapping[], column: number, value: string): ImportColumnMapping[] {
  const next = parseChoice(value, column);
  const kept = columns.filter((c) => {
    if (c.column === column) return false;
    if (!next) return true;
    if (next.target === "custom") return !(c.target === "custom" && c.fieldId === next.fieldId);
    return REPEATABLE.has(next.target) ? true : c.target !== next.target;
  });
  return (next ? [...kept, next] : kept).sort((a, b) => a.column - b.column);
}

export const hasTitle = (columns: ImportColumnMapping[]): boolean => columns.some((c) => c.target === "title");

/** Keep only status values the person actually mapped. */
export function cleanStatusMap(map: Record<string, ImportStatus | "">): Record<string, ImportStatus> {
  const out: Record<string, ImportStatus> = {};
  for (const [key, value] of Object.entries(map)) if (value) out[key] = value;
  return out;
}

/** The text fields sent alongside the file for preview and commit. */
export function mappingPayload(
  columns: ImportColumnMapping[],
  statusMap: Record<string, ImportStatus | "">,
  dateOrder: "dmy" | "mdy",
  estimateUnit: "minutes" | "hours",
): string {
  return JSON.stringify({ columns, statusMap: cleanStatusMap(statusMap), dateOrder, estimateUnit });
}
